import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Download, RotateCcw, Home, Music, VolumeX, Palette, Info } from "lucide-react";

import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import { createStoryMusicPlayer, stopAnyStoryMusic } from "../lib/storyMusic";

const TITLE_KEY = { blueprint: "blueprint", pencil: "pencil", story: "story", "colour-guide": "colour_guide" };

const COLOUR_GUIDE_STEPS = [
  { n: "①", key: "cg_step_1" },
  { n: "②", key: "cg_step_2" },
  { n: "③", key: "cg_step_3" },
  { n: "④", key: "cg_step_4" },
  { n: "⑤", key: "cg_step_5" },
  { n: "⑥", key: "cg_step_6" },
];

function ColourGuideInfoCard({ lang }) {
  return (
    <div
      data-testid="colour-guide-info-card"
      className="mt-4 bg-white rounded-3xl p-5 border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.1)]"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-lime-300 flex items-center justify-center border-2 border-white">
          <Palette size={20} strokeWidth={2.75} className="text-slate-800" />
        </div>
        <h3 className="font-heading text-xl font-bold text-slate-800">
          {t(lang, "cg_coloring_order")}
        </h3>
      </div>

      <ol className="space-y-2">
        {COLOUR_GUIDE_STEPS.map((s) => (
          <li key={s.key} className="flex items-start gap-3">
            <span className="text-2xl leading-6 text-lime-600 font-bold shrink-0 w-7 text-center">
              {s.n}
            </span>
            <div className="min-w-0">
              <p className="font-heading font-bold text-slate-800 text-base leading-snug">
                {t(lang, `${s.key}_title`)}
              </p>
              <p className="text-slate-600 text-sm font-semibold">
                {t(lang, `${s.key}_desc`)}
              </p>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-4 bg-amber-50 border-2 border-amber-200 rounded-2xl p-3">
        <div className="flex items-center gap-2 mb-1">
          <Info size={16} strokeWidth={2.75} className="text-amber-600 shrink-0" />
          <p className="font-heading font-bold text-amber-800 text-sm uppercase tracking-wide">
            {t(lang, "cg_note_heading")}
          </p>
        </div>
        <ol className="list-decimal ml-5 text-sm text-slate-700 font-semibold space-y-1">
          <li>{t(lang, "cg_note_1")}</li>
          <li>{t(lang, "cg_note_2")}</li>
        </ol>
      </div>
    </div>
  );
}

export default function Result() {
  const navigate = useNavigate();
  const { lang } = useAuth();
  const [item, setItem] = useState(null);
  const [musicOn, setMusicOn] = useState(true);
  const playerRef = useRef(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("dt_last_result");
      if (!raw) {
        navigate("/drawing", { replace: true });
        return;
      }
      setItem(JSON.parse(raw));
    } catch (_) {
      navigate("/drawing", { replace: true });
    }
  }, [navigate]);

  // Start / stop story background music.
  //
  // Contract:
  //   • Autoplays the moment the story image is on-screen (subject to
  //     browser policies — we do the best we can and swallow any
  //     NotAllowedError silently; the mute toggle in the corner is the
  //     user-visible affordance for those cases).
  //   • ALWAYS torn down when this component unmounts (route change,
  //     browser back, `Make another`, `Home`, tab hide, tab close). The
  //     cleanup path is guarded three ways: local player.stop() +
  //     stopAnyStoryMusic() singleton kill + global pagehide/visibility
  //     listeners registered inside storyMusic.js.
  useEffect(() => {
    if (!item || item.type !== "story") {
      // Any non-story result also implicitly kills any leftover music
      // from a previous story — belt and braces.
      stopAnyStoryMusic();
      return undefined;
    }
    const player = createStoryMusicPlayer({ volume: 0.18 });
    playerRef.current = player;
    if (musicOn) player.play().catch(() => { /* autoplay blocked */ });
    return () => {
      try { player.stop(); } catch (_) { /* noop */ }
      // Defensive extra: if some other player somehow claimed the
      // singleton slot in between, kill it too.
      stopAnyStoryMusic();
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // Toggle in response to mute button.
  useEffect(() => {
    const p = playerRef.current;
    if (!p) return;
    if (musicOn) p.play().catch(() => { /* ignore */ });
    else p.stop();
  }, [musicOn]);

  if (!item) return null;

  const isGif = item.mime === "image/gif";
  const ext = isGif ? "gif" : "png";
  const dataUrl = `data:${item.mime};base64,${item.image_b64}`;

  const onDownload = () => {
    try {
      const byteString = atob(item.image_b64);
      const buf = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) buf[i] = byteString.charCodeAt(i);
      const blob = new Blob([buf], { type: item.mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `drawing-teacher-${item.type}-${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Saved!");
    } catch (_) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="page-shell" data-testid="result-page">
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/drawing")}
          className="p-3 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
        >
          <ArrowLeft size={22} strokeWidth={2.5} className="text-slate-700" />
        </button>
        <h2 className="font-heading text-2xl font-bold text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)] capitalize">
          {t(lang, TITLE_KEY[item.type] || "create")}
        </h2>
        <div className="w-12" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-white rounded-3xl p-3 border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.1)]"
      >
        <img
          data-testid="result-image"
          src={dataUrl}
          alt={item.type}
          className="w-full h-auto rounded-2xl"
        />
        {item.type === "story" && (
          <button
            data-testid="story-music-toggle"
            onClick={() => setMusicOn((v) => !v)}
            aria-label={musicOn ? "Mute music" : "Play music"}
            className={
              "absolute top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center border-4 border-white shadow-[0_3px_0_rgba(0,0,0,0.18)] active:translate-y-0.5 transition " +
              (musicOn ? "bg-fuchsia-500 text-white" : "bg-white text-slate-600")
            }
          >
            {musicOn ? <Music size={20} strokeWidth={2.75} /> : <VolumeX size={20} strokeWidth={2.75} />}
          </button>
        )}
      </motion.div>

      {item.type === "colour-guide" && <ColourGuideInfoCard lang={lang} />}

      <button
        data-testid="download-btn"
        onClick={onDownload}
        className="mt-6 w-full bg-emerald-400 hover:bg-emerald-500 text-white font-heading text-xl font-bold uppercase tracking-wide py-4 rounded-full btn-squish flex items-center justify-center gap-2"
      >
        <Download size={22} strokeWidth={2.5} /> {t(lang, "download")}
      </button>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <button
          data-testid="another-btn"
          onClick={() => navigate(`/drawing/capture/${item.type}`)}
          className="bg-white border-2 border-slate-900/10 text-slate-700 font-heading font-bold uppercase py-4 rounded-full btn-squish flex items-center justify-center gap-2"
        >
          <RotateCcw size={20} strokeWidth={2.5} /> {t(lang, "make_another")}
        </button>
        <button
          data-testid="home-btn"
          onClick={() => navigate("/drawing")}
          className="bg-purple-400 hover:bg-purple-500 text-white font-heading font-bold uppercase py-4 rounded-full btn-squish flex items-center justify-center gap-2"
        >
          <Home size={20} strokeWidth={2.5} /> {t(lang, "home")}
        </button>
      </div>
    </div>
  );
}
